import { useEffect, useState } from "react";
import { fetchCatalog, fetchKits, prepareCheckout, validateCart } from "../api/catalog";
import { useCart } from "../cart/CartContext";
import { ambiguousKitStep, cartFreightMinor, lineAmount, nextKit } from "../cart/kitLine";
import StoreBoot from "../components/StoreBoot";
import StoreCredit from "../components/StoreCredit";
import StoreHeader from "../components/StoreHeader";
import StorePixels from "../components/StorePixels";
import { storePath } from "../lib/storePaths";

const checkoutBaseUrl = String(
  import.meta.env.VITE_CHECKOUT_URL || "http://localhost:5174"
).replace(/\/$/, "");
const moneyFormatters = new Map();

function formatMoney(amountMinor, currency = "BRL") {
  const key = `${currency}:pt-BR`;
  if (!moneyFormatters.has(key)) {
    moneyFormatters.set(
      key,
      new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency,
      })
    );
  }
  return moneyFormatters.get(key).format(Number(amountMinor) / 100);
}

function itemOptions(item) {
  const values = Object.values(item.options || {}).filter(
    (value) => value && typeof value !== "boolean"
  );
  return values.length > 0 ? values.join(" / ") : item.variantTitle;
}

function availabilityKey(item) {
  return `${item.variantId}:${item.offerId || ""}`;
}

function CartSummary({
  storeSlug,
  availabilityIssues,
  checkingAvailability,
  onAvailabilityChange,
}) {
  const { items, itemCount, subtotalMinor, currency } = useCart();
  const freightMinor = cartFreightMinor(items);
  const hasKit = items.some((item) => item.offerId);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const startCheckout = async () => {
    setSubmitting(true);
    setError("");
    try {
      const availability = await validateCart(storeSlug, items);
      onAvailabilityChange(availability);
      if (!availability.available) {
        setError(
          "Alguns produtos não estão mais disponíveis. Remova-os ou ajuste a quantidade antes de continuar."
        );
        setSubmitting(false);
        return;
      }
      const fingerprint = items
        .map((item) => `${item.variantId}:${item.offerId || ""}:${item.affiliateCode || ""}:${item.quantity}:${item.amountMinor}`)
        .sort()
        .join("|");
      const storageKey = `atomicpay-checkout-key:${storeSlug}:${fingerprint}`;
      let idempotencyKey = sessionStorage.getItem(storageKey);
      if (!idempotencyKey) {
        idempotencyKey = crypto.randomUUID();
        sessionStorage.setItem(storageKey, idempotencyKey);
      }
      const checkout = await prepareCheckout(storeSlug, items, {
        idempotencyKey,
      });
      window.location.assign(
        `${checkoutBaseUrl}/commerce/${encodeURIComponent(
          checkout.checkoutToken
        )}`
      );
    } catch (checkoutError) {
      console.error(JSON.stringify({
        event: "commerce.checkout.prepare_failed",
        code: checkoutError?.code || null,
        status: checkoutError?.status || null,
        message: checkoutError?.message || null,
        lines: items.map((item) => ({
          offerId: item.offerId || null,
          variantId: item.variantId,
          quantity: item.quantity,
          amountMinor: item.amountMinor,
        })),
      }));
      if (
        [
          "product_unavailable",
          "inventory_unavailable",
          "insufficient_stock",
          "inventory_changed",
        ].includes(checkoutError.code)
      ) {
        try {
          onAvailabilityChange(await validateCart(storeSlug, items));
        } catch {
          // Keep the authoritative checkout error when revalidation also fails.
        }
        setError(
          "A disponibilidade mudou. Revise os produtos antes de continuar."
        );
      } else {
        setError(checkoutError.message);
      }
      setSubmitting(false);
    }
  };

  return (
    <aside className="cart-summary" aria-live="polite">
      <div>
        <span>Subtotal</span>
        <span key={itemCount}>
          {itemCount} {itemCount === 1 ? "item" : "itens"}
        </span>
      </div>
      {hasKit && (
        <div>
          <span>Frete</span>
          <span>{freightMinor > 0 ? formatMoney(freightMinor, currency) : "Grátis"}</span>
        </div>
      )}
      <div className="cart-summary__total">
        <strong>Total</strong>
        <strong>{formatMoney(subtotalMinor + freightMinor, currency)}</strong>
      </div>
      {error && (
        <p className="cart-summary__error" role="alert">
          {error}
        </p>
      )}
      {!error && availabilityIssues.size > 0 && (
        <p className="cart-summary__error" role="alert">
          Revise os produtos indisponíveis antes de continuar.
        </p>
      )}
      <button
        type="button"
        className="cart-checkout-button"
        disabled={
          submitting ||
          checkingAvailability ||
          items.length === 0 ||
          availabilityIssues.size > 0
        }
        onClick={startCheckout}
      >
        {submitting
          ? "Preparando checkout..."
          : checkingAvailability
            ? "Verificando disponibilidade..."
            : "Ir para o checkout"}
      </button>
      <p>
        {hasKit && freightMinor > 0
          ? "O frete é cobrado uma vez neste pedido e não entra na partilha."
          : hasKit
            ? "Todos os kits deste carrinho têm frete grátis."
            : "Frete e condições de pagamento serão definidos no checkout."}
      </p>
    </aside>
  );
}

export default function CartPage({ storeSlug }) {
  const { items, setQuantity, replaceKit, removeItem } = useCart();
  const [productKits, setProductKits] = useState(new Map());
  const [store, setStore] = useState(null);
  const [storeReady, setStoreReady] = useState(false);
  const [availabilityIssues, setAvailabilityIssues] = useState(new Map());
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const storeUrl = storePath("", storeSlug);

  useEffect(() => {
    const controller = new AbortController();
    fetchCatalog(storeSlug, {
      page: 1,
      limit: 1,
      signal: controller.signal,
    })
      .then((result) => {
        setStore(result.store);
        document.title = `Carrinho | ${result.store.name}`;
      })
      .catch(() => {})
      .finally(() => {
        if (!controller.signal.aborted) setStoreReady(true);
      });
    return () => controller.abort();
  }, [storeSlug]);

  const applyAvailability = (result) => {
    setAvailabilityIssues(
      new Map(
        (result?.items || [])
          .filter((item) => !item.available)
          .map((item) => [availabilityKey(item), item])
      )
    );
  };

  useEffect(() => {
    if (items.length === 0) {
      setAvailabilityIssues(new Map());
      setCheckingAvailability(false);
      return undefined;
    }
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      setCheckingAvailability(true);
      validateCart(storeSlug, items, { signal: controller.signal })
        .then(applyAvailability)
        .catch((requestError) => {
          if (requestError.name !== "AbortError") {
            setAvailabilityIssues(new Map());
          }
        })
        .finally(() => {
          if (!controller.signal.aborted) setCheckingAvailability(false);
        });
    }, 250);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [items, storeSlug]);

  const kitProductIds = [...new Set(items.filter((item) => item.offerId).map((item) => item.productId))]
    .sort()
    .join("|");

  useEffect(() => {
    if (!kitProductIds) {
      setProductKits(new Map());
      return undefined;
    }
    const controller = new AbortController();
    Promise.all(
      kitProductIds.split("|").map(async (productId) => {
        try {
          const kits = await fetchKits(storeSlug, productId, { signal: controller.signal });
          return [productId, kits];
        } catch (requestError) {
          if (requestError?.name === "AbortError") throw requestError;
          return [productId, []];
        }
      })
    )
      .then((entries) => {
        if (!controller.signal.aborted) setProductKits(new Map(entries));
      })
      .catch((requestError) => {
        if (requestError?.name !== "AbortError") setProductKits(new Map());
      });
    return () => controller.abort();
  }, [kitProductIds, storeSlug]);

  if (!storeReady) return <StoreBoot />;

  return (
    <div className="storefront">
      <StorePixels store={store} />
      <StoreHeader store={store} storeSlug={storeSlug} />

      <main className="cart-page">
        <nav className="breadcrumb" aria-label="Navegação estrutural">
          <a href={storeUrl}>
            <svg viewBox="0 0 24 24" aria-hidden>
              <path d="m15 18-6-6 6-6" />
            </svg>
            Continuar comprando
          </a>
        </nav>

        <div className="cart-page__heading">
          <div>
            <p className="catalog__eyebrow">Sua seleção</p>
            <h1>Carrinho</h1>
          </div>
        </div>

        {items.length === 0 ? (
          <section className="empty-cart">
            <div className="empty-cart__icon" aria-hidden>
              <svg viewBox="0 0 24 24">
                <path d="M3 4h2l2.2 10.2a2 2 0 0 0 2 1.6h7.7a2 2 0 0 0 2-1.6L20.2 8H6.1M9.5 20a.5.5 0 1 1-1 0 .5.5 0 0 1 1 0Zm8 0a.5.5 0 1 1-1 0 .5.5 0 0 1 1 0Z" />
              </svg>
            </div>
            <h2>Seu carrinho está vazio</h2>
            <p>Explore a loja e escolha os produtos que deseja comprar.</p>
            <a href={storeUrl}>Ver produtos</a>
          </section>
        ) : (
          <div className="cart-layout">
            <section className="cart-items" aria-label="Produtos no carrinho">
              {items.map((item) => {
                const availabilityIssue = availabilityIssues.get(availabilityKey(item));
                const kitsForProduct = productKits.get(item.productId) || [];
                const previousKit = item.offerId ? nextKit(kitsForProduct, item.offerId, -1) : null;
                const followingKit = item.offerId ? nextKit(kitsForProduct, item.offerId, 1) : null;
                const kitChoiceNeeded = item.offerId && (
                  ambiguousKitStep(kitsForProduct, item.offerId, -1)
                  || ambiguousKitStep(kitsForProduct, item.offerId, 1)
                );
                const productUrl = storePath(
                  `/products/${encodeURIComponent(item.productHandle)}`,
                  storeSlug
                );
                const atMaximum =
                  item.maxQuantity != null &&
                  item.quantity >= item.maxQuantity;

                return (
                  <article className="cart-item" key={item.id}>
                    <a className="cart-item__image" href={productUrl}>
                      {item.image ? (
                        <img src={item.image} alt="" loading="lazy" />
                      ) : (
                        <span aria-hidden>{item.title.slice(0, 1)}</span>
                      )}
                    </a>

                    <div className="cart-item__information">
                      <a href={productUrl}>{item.title}</a>
                      <p>{item.offerId ? `Kit de ${item.kitQuantity}` : itemOptions(item)}</p>
                      {item.sku && <small>SKU {item.sku}</small>}
                      {availabilityIssue && (
                        <p className="cart-item__availability" role="alert">
                          {availabilityIssue.reason === "insufficient_stock"
                            ? `Apenas ${availabilityIssue.availableQuantity} unidade(s) disponível(is). Ajuste a quantidade ou remova o produto.`
                            : "Produto indisponível. Remova-o do carrinho para continuar."}
                        </p>
                      )}
                      <button
                        type="button"
                        className="cart-item__remove"
                        onClick={() => removeItem(item.id)}
                      >
                        Remover
                      </button>
                    </div>

                    {item.offerId ? (
                      <div className="quantity-control quantity-control--kit">
                        <button
                          type="button"
                          disabled={!previousKit}
                          aria-label={`Kit anterior de ${item.title}`}
                          onClick={() => replaceKit(item.id, previousKit)}
                        >
                          −
                        </button>
                        <span aria-label={`Kit de ${item.kitQuantity}`}>
                          {item.kitQuantity}
                        </span>
                        <button
                          type="button"
                          disabled={!followingKit}
                          aria-label={`Próximo kit de ${item.title}`}
                          onClick={() => replaceKit(item.id, followingKit)}
                        >
                          +
                        </button>
                      </div>
                    ) : (
                      <div className="quantity-control">
                        <button
                          type="button"
                          aria-label={`Diminuir quantidade de ${item.title}`}
                          onClick={() =>
                            setQuantity(item.id, item.quantity - 1)
                          }
                        >
                          −
                        </button>
                        <span aria-label={`Quantidade: ${item.quantity}`}>
                          {item.quantity}
                        </span>
                        <button
                          type="button"
                          disabled={atMaximum}
                          aria-label={`Aumentar quantidade de ${item.title}`}
                          onClick={() =>
                            setQuantity(item.id, item.quantity + 1)
                          }
                        >
                          +
                        </button>
                      </div>
                    )}

                    <div className="cart-item__amount">
                      <strong>
                        {formatMoney(lineAmount(item), item.currency)}
                      </strong>
                      {item.offerId ? (
                        <small>Preço do kit, não o unitário vezes a quantidade</small>
                      ) : item.quantity > 1 ? (
                        <small>
                          {formatMoney(item.amountMinor, item.currency)} cada
                        </small>
                      ) : null}
                      {kitChoiceNeeded && (
                        <small>Há mais de um kit nessa quantidade. Escolha na página do produto.</small>
                      )}
                    </div>
                  </article>
                );
              })}
            </section>

            <CartSummary
              storeSlug={storeSlug}
              availabilityIssues={availabilityIssues}
              checkingAvailability={checkingAvailability}
              onAvailabilityChange={applyAvailability}
            />
          </div>
        )}
      </main>

      <footer className="store-footer">
        <StoreCredit />
      </footer>
    </div>
  );
}
